import {
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import useBuyAgainOrder from "@features/ecommerce/hooks/useBuyAgainOrder";
import React, { useCallback, useEffect } from "react";
import { OrderLineItemDto } from "@lib/ShiOneClient";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useOrderTelemetrySection } from "@features/orders-features/components/order-drawer/orderTelemetrySection";
import BuyAgainResultDialog from "@features/ecommerce/components/BuyAgainResultDialog";
import useBuyAgainResultDialog from "@features/ecommerce/components/useBuyAgainResultDialog";
import { useOrdersCartHandoff } from "@features/ecommerce/cart/useOrdersCartHandoff";

export function BuyAgainButton({
  items,
  telemetrySection,
  telemetryId,
}: Readonly<{
  items: OrderLineItemDto[];
  /** Explicit section override; when omitted, resolved from `useOrderTelemetrySection`. */
  telemetrySection?: string;
  telemetryId?: string;
}>) {
  const {
    mutate: buyAgain,
    isPending,
    isError,
    clearBuyAgainResult,
  } = useBuyAgainOrder();
  const { canSaveToCart, handoffBuyAgainSuccess } = useOrdersCartHandoff();

  const contextSection = useOrderTelemetrySection();
  const section = telemetrySection ?? contextSection;

  useEffect(() => {
    if (isError) {
      clearBuyAgainResult();
    }
  }, [isError, clearBuyAgainResult]);

  const { show: showLocalResultDialog, dialogProps: resultDialogProps } =
    useBuyAgainResultDialog(clearBuyAgainResult);

  const onBuyAgain = useCallback(
    () =>
      buyAgain(items, {
        onSuccess: (result) => {
          const outcome = handoffBuyAgainSuccess(result);
          if (outcome === "stayed" && result.failedCount === 0) {
            return;
          }

          if (
            outcome === "no-cart-view" &&
            result.successCount > 0 &&
            result.failedCount === 0
          ) {
            return;
          }

          showLocalResultDialog(result);
        },
      }),
    [
      buyAgain,
      handoffBuyAgainSuccess,
      items,
      showLocalResultDialog,
    ],
  );

  return (
    <>
      <Tooltip title="Buy Again">
        <span>
          <IconButton
            aria-label="Buy Again"
            sx={{ color: "action.active" }}
            size="small"
            disabled={isPending || !canSaveToCart}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onBuyAgain();
            }}
            {...telemetryProps({
              name: "orders_buy_again",
              section,
              label: "Buy again",
              ...(telemetryId !== undefined ? { id: telemetryId } : {}),
            })}
          >
            {isPending ? (
              <CircularProgress size={18} />
            ) : (
              <ShoppingCartOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <BuyAgainResultDialog {...resultDialogProps} />
    </>
  );
}
